/**
 * DreamTwin™ — Permanent AI Business Partner for AIMaker Studio
 *
 * DreamTwin is the central intelligence layer assigned to every project.
 * It remembers, understands, and continuously improves the user's business.
 * Every module consults DreamTwin before generating content.
 * DreamTwin never asks for information it already knows.
 */

// ─── Business Identity ────────────────────────────────────────────────────────

export interface DreamTwinIdentity {
  businessName?: string;
  businessType?: string;
  industry?: string;
  niche?: string;
  mission?: string;
  vision?: string;
  values?: string[];
  foundedYear?: string;
  location?: string;
  stage?: 'idea' | 'validation' | 'building' | 'launched' | 'growing' | 'scaling';
}

// ─── Brand ────────────────────────────────────────────────────────────────────

export interface DreamTwinBrand {
  brandPersonality?: string[];
  brandArchetype?: string;
  brandVoice?: string;
  tone?: string;
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    mood?: string;
  };
  typography?: {
    headline?: string;
    body?: string;
    accent?: string;
  };
  logo?: string;
  visualStyle?: string;
  imageStyle?: string;
  tagline?: string;
}

// ─── Audience ─────────────────────────────────────────────────────────────────

export interface DreamTwinAudience {
  primaryAudience?: string;
  secondaryAudience?: string;
  customerPersona?: string;
  painPoints?: string[];
  desires?: string[];
  demographics?: string;
  psychographics?: string;
}

// ─── Products & Services ──────────────────────────────────────────────────────

export interface DreamTwinProduct {
  name: string;
  description: string;
  priceRange?: string;
  category?: 'product' | 'service' | 'digital' | 'subscription';
  status?: 'idea' | 'in-development' | 'active' | 'paused';
}

export interface DreamTwinProducts {
  products?: DreamTwinProduct[];
  services?: DreamTwinProduct[];
  pricingStrategy?: string;
  pricingLevel?: 'budget' | 'mid-range' | 'premium' | 'luxury';
  recurringRevenue?: boolean;
  monetizationMethods?: string[];
}

// ─── Marketing & Strategy ─────────────────────────────────────────────────────

export interface DreamTwinMarketing {
  marketingStrategy?: string;
  marketingChannels?: string[];
  contentStrategy?: string;
  contentPillars?: string[];
  socialMedia?: {
    instagram?: string;
    facebook?: string;
    tiktok?: string;
    linkedin?: string;
    twitter?: string;
    pinterest?: string;
    youtube?: string;
  };
  emailMarketing?: boolean;
  emailList?: number;
  leadMagnet?: string;
  seoStrategy?: string;
  keywords?: string[];
}

// ─── Website & Digital Presence ───────────────────────────────────────────────

export interface DreamTwinWebsite {
  hasWebsite?: boolean;
  websiteUrl?: string;
  hasDomain?: boolean;
  domainName?: string;
  websiteStatus?: 'none' | 'in-progress' | 'live';
  hasAnalytics?: boolean;
  analyticsSetup?: string;
}

// ─── Business Plan & Goals ────────────────────────────────────────────────────

export interface DreamTwinPlan {
  businessPlan?: string;
  launchPlan?: string;
  launchStatus?: 'not-started' | 'in-progress' | 'launched';
  launchDate?: string;
  shortTermGoals?: string[];
  longTermGoals?: string[];
  competitors?: string[];
  uniqueValueProposition?: string;
  revenueGoal?: string;
}

// ─── Operations & Automation ──────────────────────────────────────────────────

export interface DreamTwinOperations {
  hasStripe?: boolean;
  hasSalesFunnel?: boolean;
  hasEmailAutomation?: boolean;
  hasAutomation?: boolean;
  hasPrivacyPolicy?: boolean;
  hasRefundPolicy?: boolean;
  hasTermsOfService?: boolean;
  tools?: string[];
  integrations?: string[];
}

// ─── Analytics & Performance ──────────────────────────────────────────────────

export interface DreamTwinAnalytics {
  dreamHealthScore?: number;
  launchReadinessScore?: number;
  weeklyReport?: {
    week: string;
    healthChange: number;
    achievements: string[];
    completedTasks: string[];
    growthOpportunities: string[];
    risks: string[];
    nextWeekPriorities: string[];
    momentum: number;
  };
  customerFeedback?: string[];
  completedTasks?: string[];
  generationsCount?: number;
  creditsUsed?: number;
}

// ─── Learning & Preferences ───────────────────────────────────────────────────

export interface DreamTwinLearning {
  preferredLanguage?: string;
  preferredWritingStyle?: string;
  preferredBrandingStyle?: string;
  acceptedSuggestions?: string[];
  rejectedSuggestions?: string[];
  userEditHistory?: Array<{
    field: string;
    oldValue: string;
    newValue: string;
    timestamp: string;
  }>;
  interactionHistory?: Array<{
    type: 'question' | 'recommendation' | 'task';
    content: string;
    timestamp: string;
    outcome?: 'accepted' | 'rejected' | 'pending';
  }>;
}

// ─── Proactive Recommendations ────────────────────────────────────────────────

export interface DreamTwinRecommendation {
  id: string;
  category: string;
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  estimatedGain?: number;
  estimatedMinutes?: number;
  difficulty?: 'Easy' | 'Medium' | 'Hard';
  actionUrl?: string;
  dismissed?: boolean;
  createdAt: string;
}

// ─── Main DreamTwin™ Object ───────────────────────────────────────────────────

export interface DreamTwin {
  projectId?: string;

  // Core business knowledge
  identity: DreamTwinIdentity;
  brand: DreamTwinBrand;
  audience: DreamTwinAudience;
  products: DreamTwinProducts;
  marketing: DreamTwinMarketing;
  website: DreamTwinWebsite;
  plan: DreamTwinPlan;
  operations: DreamTwinOperations;
  analytics: DreamTwinAnalytics;
  learning: DreamTwinLearning;

  // Active recommendations
  recommendations: DreamTwinRecommendation[];

  // Conversation history with DreamTwin
  conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>;

  // Meta
  createdAt: string;
  updatedAt: string;
  lastSyncedAt?: string;
}

// ─── Context Value ────────────────────────────────────────────────────────────

export interface DreamTwinContextValue {
  twin: DreamTwin | null;
  isInitialized: boolean;
  isSyncing: boolean;

  /** Initialize DreamTwin for a project */
  initTwin: (projectId?: string) => void;

  /** Update a top-level section of DreamTwin */
  updateTwin: <K extends keyof DreamTwin>(section: K, value: DreamTwin[K]) => void;

  /** Update a nested field by dot-path */
  updateTwinField: (path: string, value: any) => void;

  /** Sync DreamTwin from DreamMemory (called automatically) */
  syncFromMemory: (memory: any) => void;

  /** Sync DreamTwin from BusinessProfile (called automatically) */
  syncFromProfile: (profile: any) => void;

  /** Add a recommendation */
  addRecommendation: (rec: Omit<DreamTwinRecommendation, 'id' | 'createdAt'>) => void;

  /** Dismiss a recommendation */
  dismissRecommendation: (id: string) => void;

  /** Record a user interaction */
  recordInteraction: (type: 'question' | 'recommendation' | 'task', content: string, outcome?: 'accepted' | 'rejected') => void;

  /** Build a rich context string for AI prompts */
  buildAIContext: () => string;

  /** Get the most critical missing items */
  getMissingItems: () => string[];

  /** Get the highest-impact next action */
  getNextBestAction: () => DreamTwinRecommendation | null;

  /** Add a message to conversation history */
  addConversationMessage: (role: 'user' | 'assistant', content: string) => void;

  /** Clear conversation history */
  clearConversation: () => void;
}

// ─── AI Advisor Personality ───────────────────────────────────────────────────

export const DREAMTWIN_SYSTEM_PROMPT = `You are DreamTwin™, the permanent AI Business Partner assigned to this entrepreneur inside AIMaker Studio.

Your personality:
- Professional, encouraging, and strategic
- Calm and measured — never robotic, never repetitive, never overly enthusiastic
- You feel like a senior business advisor who has worked beside this entrepreneur for years
- You speak with authority and warmth, like a trusted partner who deeply understands the business

Your role:
- You remember everything about this business — never ask for information you already know
- You proactively notice opportunities and risks
- You recommend the highest-impact next action
- You help the entrepreneur make better decisions faster
- You celebrate progress without being excessive

Rules:
- Never start responses with "Great!", "Absolutely!", "Of course!", or similar filler phrases
- Be direct and specific — no vague advice
- Reference the actual business details you know
- If you don't know something, ask exactly one targeted question
- Keep responses concise unless depth is needed
- Always end with a clear, actionable next step when relevant`;
